import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../helpers'

export type VendorComplianceStatus =
    | 'COMPLIANT'
    | 'NON_COMPLIANT'
    | 'EXPIRED'
    | 'EXPIRING_SOON'
    | 'PENDING_REVIEW'
    | 'NO_COI'

export interface ComplianceStats {
    total_vendors: number
    compliant: number
    action_required: number
    expiring_soon: number
}

export interface VendorCompliance {
    vendor_id: number
    vendor_name: string
    services: string[]
    status: VendorComplianceStatus
    coi_count: number
    expiring_soon_count: number
    earliest_expiration: string | null
    compliance_issues: string[]
    apartment_id?: number
    apartment_name?: string
}

export interface ComplianceDashboardResponse {
    stats: ComplianceStats
    vendors: VendorCompliance[]
}

// New types for organization-level compliance dashboard
export interface PropertyCompliance {
    apartment_id: number
    apartment_name: string
    total_vendors: number
    compliant: number
    action_required: number
    expiring_soon: number
}

export interface OrgComplianceDashboardResponse {
    properties: PropertyCompliance[]
    totals: ComplianceStats
}

export interface OrgComplianceParams {
    organization?: number | null
}

const QUERY_KEY = ['orgComplianceDashboard']

export const useOrgComplianceDashboard = (params?: OrgComplianceParams) => {
    // Build query string
    const buildQueryString = () => {
        if (!params?.organization) return ''
        const searchParams = new URLSearchParams()
        searchParams.append('organization', params.organization.toString())
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    const { data: dashboardData, isLoading, error, refetch } = useQuery<
        AxiosResponse<OrgComplianceDashboardResponse>,
        Error
    >({
        queryKey: [...QUERY_KEY, params?.organization],
        queryFn: () =>
            axiosInstance.get(
                `/company/organization-compliance-dashboard/${buildQueryString()}`,
            ),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    return {
        properties: dashboardData?.data?.properties || [],
        totals: dashboardData?.data?.totals || null,
        isLoading,
        error,
        refetch,
    }
}

// Apartment-level compliance dashboard types
export interface ApartmentComplianceDashboardResponse {
    apartment_id: number
    apartment_name: string
    stats: ComplianceStats
    vendors: VendorCompliance[]
}

export interface ApartmentComplianceParams {
    apartmentId?: number | null
}

const APARTMENT_QUERY_KEY = ['apartmentComplianceDashboard']

export const useApartmentComplianceDashboard = (
    params?: ApartmentComplianceParams,
) => {
    // Build query string
    const buildQueryString = () => {
        if (!params?.apartmentId) return ''
        const searchParams = new URLSearchParams()
        searchParams.append('apartment_id', params.apartmentId.toString())
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    const { data: dashboardData, isLoading, error, refetch } = useQuery<
        AxiosResponse<ApartmentComplianceDashboardResponse>,
        Error
    >({
        queryKey: [...APARTMENT_QUERY_KEY, params?.apartmentId],
        queryFn: () =>
            axiosInstance.get(
                `/company/apartment-compliance-dashboard/${buildQueryString()}`,
            ),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.apartmentId,
    })

    return {
        apartmentId: dashboardData?.data?.apartment_id || null,
        apartmentName: dashboardData?.data?.apartment_name || '',
        stats: dashboardData?.data?.stats || null,
        vendors: dashboardData?.data?.vendors || [],
        isLoading,
        error,
        refetch,
    }
}
